import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { StatusBadge } from '../components/ui/StatusBadge'
import ProgressBar from '../components/ui/ProgressBar'
import FileUpload from '../components/files/FileUpload'
import FileList from '../components/files/FileList'
import Comments from '../components/files/Comments'
import AIReportButton from '../components/ai/AIReportButton'
import Modal from '../components/ui/Modal'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChevronRight, FileText, MessageSquare, BarChart3, Plus, Trash2, Pencil, Sparkles, Save, Edit3, Download, User, Copy, CheckCircle2, ChevronUp, ChevronDown, Users, Swords, Trophy, Upload, CheckCircle, AlertCircle, Loader, Target, Lock, Unlock, ClipboardList, UserCheck, Quote, Building2, BarChart2, Lightbulb } from 'lucide-react'
import DRLTab from '../components/etapa0/DRLTab'
import DRLProgressBars from '../components/etapa0/DRLProgressBars'
import CajaDRL from '../components/etapa0/CajaDRL'
import StakeholdersTab from '../components/etapa0/StakeholdersTab'
import AfirmacionesTab from '../components/etapa0/AfirmacionesTab'
import MiModeloNegocio from '../components/etapa0/MiModeloNegocio'
import DemoBanner from '../components/caja/DemoBanner'
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx'
import * as XLSX from 'xlsx'

// Phase-level guidance for Consultoría en una Caja
const CAJA_PHASE_META = {
  0: {
    icon: '🏢', color: 'from-slate-600 to-slate-700',
    description: 'Entiende cómo funciona tu negocio hoy: modelo, fuentes de ingreso y recursos clave.',
    tip: 'Este diagnóstico inicial es la base de toda la estrategia. Sé honesto sobre la situación real de tu empresa.',
  },
  1: {
    icon: '🔍', color: 'from-blue-600 to-blue-700',
    description: 'Analiza el entorno externo (PESTEL), la industria (Porter), lo interno y construye tu DOFA.',
    tip: 'Este análisis revela dónde están las amenazas y oportunidades reales de tu sector.',
  },
  2: {
    icon: '🧭', color: 'from-violet-600 to-violet-700',
    description: 'Define (o redefine) tu misión, visión, valores y los objetivos estratégicos de los próximos 3 años.',
    tip: 'Las decisiones más importantes de tu empresa se toman aquí. Tómate el tiempo necesario.',
  },
  3: {
    icon: '📊', color: 'from-cyan-600 to-cyan-700',
    description: 'Mide el tamaño del mercado, analiza productos, canales y la participación de tus competidores.',
    tip: 'Los datos de mercado convierten suposiciones en decisiones basadas en evidencia.',
  },
  4: {
    icon: '📈', color: 'from-emerald-600 to-emerald-700',
    description: 'Define la estrategia específica para cada unidad de negocio: posicionamiento, portafolio y precio.',
    tip: 'Cada unidad de negocio puede requerir una estrategia diferente. Aquí las distingues.',
  },
  5: {
    icon: '🚀', color: 'from-orange-600 to-orange-700',
    description: 'Traduce la estrategia en un plan de implementación con responsables, fechas y KPIs.',
    tip: 'Una estrategia sin plan de ejecución es solo un deseo. Este es el paso que lo hace real.',
  },
  6: {
    icon: '✨', color: 'from-brand-600 to-brand-700',
    description: 'Consolida todas las unidades en una narrativa corporativa integrada y coherente.',
    tip: 'El resultado final: una estrategia corporativa unificada lista para presentar a tu equipo.',
  },
}

export default function PhasePage() {
  const { id, buId, phaseId } = useParams()
  const navigate = useNavigate()
  const { isNVC, isAdmin, profile, isCaja } = useAuth()
  const [phase, setPhase]         = useState(null)
  const [buName, setBuName]       = useState('')
  const [consultancyName, setConsultancyName] = useState('')
  const [isDemo, setIsDemo] = useState(false)
  const [sections, setSections]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [activeTab, setActiveTab] = useState('sections')

  // ── Strategic Objective (project-level "Análisis Interno Corporativo" phase) ──
  const CORP_REAL_YEARS = [new Date().getFullYear() - 3, new Date().getFullYear() - 2, new Date().getFullYear() - 1]
  const CORP_EMPTY_OBJ  = { vision: '', mision: '', declaracion: '', estrategia: '', generated_text: '' }
  const [corpObj, setCorpObj]                 = useState(null)
  const [corpObjForm, setCorpObjForm]         = useState(CORP_EMPTY_OBJ)
  const [corpMetrics, setCorpMetrics]         = useState({})
  const [savingCorpObj, setSavingCorpObj]     = useState(false)
  const [corpObjSaved, setCorpObjSaved]       = useState(false)
  const [generatingCorpObj, setGeneratingCorpObj] = useState(false)
  const [corpEstrategiaPuntos, setCorpEstrategiaPuntos] = useState([])  // [{id, texto}]
  const [newEstrategiaPoint, setNewEstrategiaPoint]     = useState('')

  // New section modal
  const [sectionOpen, setSectionOpen]   = useState(false)
  const [sectionForm, setSectionForm]   = useState({ name: '', description: '', assignee_id: '' })
  const [savingSection, setSavingSection] = useState(false)

  // Edit section modal
  const [editSection, setEditSection]   = useState(null)
  const [editSectionForm, setEditSectionForm] = useState({ name: '', description: '', assignee_id: '' })

  // Profiles for assignee dropdown
  const [profiles, setProfiles] = useState([])
  const [savingEdit, setSavingEdit]     = useState(false)

  // Consumer profiles
  const [consumerProfiles, setConsumerProfiles] = useState([])
  const [profileOpen, setProfileOpen]           = useState(false)
  const [profileForm, setProfileForm]           = useState({ name: '', description: '' })
  const [savingProfile, setSavingProfile]       = useState(false)

  // Competitors
  const [competitors, setCompetitors]             = useState([])
  const [competitorOpen, setCompetitorOpen]       = useState(false)
  const [competitorForm, setCompetitorForm]       = useState({ name: '', description: '' })
  const [editCompetitor, setEditCompetitor]       = useState(null)
  const [editCompetitorForm, setEditCompetitorForm] = useState({ name: '', description: '' })
  const [savingCompetitor, setSavingCompetitor]   = useState(false)

  // Duplicate section modal
  const [copySection, setCopySection]     = useState(null)   // section being duplicated
  const [allPhases, setAllPhases]         = useState([])     // all phases in consultancy
  const [copyTargetId, setCopyTargetId]   = useState('')     // target phase id
  const [copyingSection, setCopyingSection] = useState(false)
  const [copyDone, setCopyDone]           = useState(false)  // success flash

  // AI reports tab
  const [aiReports, setAiReports]       = useState([])
  const [userNotes, setUserNotes]       = useState('')
  const [savedNotes, setSavedNotes]     = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [savingNotes, setSavingNotes]   = useState(false)
  const [notesRecordId, setNotesRecordId] = useState(null)

  const [resultText, setResultText]         = useState('')
  const [savedResult, setSavedResult]       = useState('')
  const [editingResult, setEditingResult]   = useState(false)
  const [savingResult, setSavingResult]     = useState(false)
  const [resultRecordId, setResultRecordId] = useState(null)
  const [resultFileKey, setResultFileKey]   = useState(0)

  // Excel import (Análisis del Mercado)
  const xlsxInputRef = useRef(null)
  const [xlsxImporting, setXlsxImporting]   = useState(false)
  const [xlsxResult, setXlsxResult]         = useState(null)  // { ok, message, details[] }

  const isProjectPhase = !buId  // true when accessed from project level (no BU)

  const [phaseLocked, setPhaseLocked] = useState(false)
  const [lockingPhase, setLockingPhase] = useState(false)

  useEffect(() => { fetchData() }, [phaseId])
  // Switch to 'drl' as default tab for Etapa 0 once phase data loads
  useEffect(() => {
    if (phase?.phase_order === 0) setActiveTab('mi_modelo')
  }, [phase?.phase_order])

  async function fetchData() {
    setLoading(true)
    const queries = [
      supabase.from('phases').select('*').eq('id', phaseId).single(),
      supabase.from('sections').select('*, section_assignee:profiles!sections_assignee_id_fkey(id, full_name), tasks(id, status)').eq('phase_id', phaseId).order('section_order'),
      supabase.from('ai_reports').select('*').eq('entity_type', 'phase').eq('entity_id', phaseId).order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name').in('role', ['admin', 'consultant', 'client']),
      // All phases in this consultancy (for the copy-to dropdown)
      supabase.from('phases').select('id, name, business_unit_id, business_units(name)').eq('consultancy_id', id).order('phase_order'),
    ]
    if (buId) queries.push(supabase.from('business_units').select('name').eq('id', buId).single())

    const [{ data: ph }, { data: secs }, { data: reports }, { data: profs }, { data: cPhases }, buResult] = await Promise.all(queries)
    setProfiles(profs ?? [])
    setAllPhases(cPhases ?? [])
    setPhase(ph)
    setPhaseLocked(ph?.is_locked ?? false)
    setSections(secs ?? [])

    // Load consumer profiles (with their sub-sections) for this phase
    const { data: cProfiles } = await supabase
      .from('consumer_profiles')
      .select('*, sections(id, name, status, section_order, tasks(id, status))')
      .eq('phase_id', phaseId)
      .order('profile_order')
    setConsumerProfiles((cProfiles ?? []).map(p => ({
      ...p,
      sections: (p.sections ?? []).sort((a, b) => a.section_order - b.section_order),
    })))

    // Load competitors (with their sub-sections) for this phase
    const { data: comps } = await supabase
      .from('competitors')
      .select('*, sections(id, name, status, section_order, tasks(id, status))')
      .eq('phase_id', phaseId)
      .order('competitor_order')
    setCompetitors((comps ?? []).map(c => ({
      ...c,
      sections: (c.sections ?? []).sort((a, b) => a.section_order - b.section_order),
    })))
    setBuName(buResult?.data?.name ?? '')

    // Consultancy display name for the breadcrumb chip ("Proyecto: ...")
    // + flag is_demo (para mostrar DemoBanner si está navegando la demo)
    supabase.from('consultancies').select('client_company, name, is_demo').eq('id', id).single()
      .then(({ data }) => {
        setConsultancyName(data?.client_company || data?.name || '')
        setIsDemo(!!data?.is_demo)
      })

    if (reports) {
      const agentReports = reports.filter(r => r.report_type === 'agent_generated')
      const userNoteRecord = reports.find(r => r.report_type === 'user_notes')
      const resultRecord   = reports.find(r => r.report_type === 'resultado')
      setAiReports(agentReports)
      if (userNoteRecord) {
        setSavedNotes(userNoteRecord.content ?? '')
        setUserNotes(userNoteRecord.content ?? '')
        setNotesRecordId(userNoteRecord.id)
      }
      if (resultRecord) {
        setSavedResult(resultRecord.content ?? '')
        setResultText(resultRecord.content ?? '')
        setResultRecordId(resultRecord.id)
      }
    }

    if (secs?.length) {
      const completed = secs.filter(s => s.status === 'completed').length
      const progress = Math.round((completed / secs.length) * 100)
      await supabase.from('phases').update({ progress }).eq('id', phaseId)
    }

    // Load corporate strategic objective (only for project-level internal analysis phases)
    if (ph && !ph.business_unit_id) {
      const normPh = (ph.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      if (normPh.includes('interno')) {
        const { data: co } = await supabase.from('strategic_objectives').select('*').eq('phase_id', phaseId).maybeSingle()
        if (co) {
          setCorpObj(co)
          setCorpObjForm({ vision: co.vision ?? '', mision: co.mision ?? '', declaracion: co.declaracion ?? '', estrategia: co.estrategia ?? '', generated_text: co.generated_text ?? '' })
          // Parse estrategia as JSON list, fall back to legacy plain text
          try {
            const parsed = JSON.parse(co.estrategia ?? '[]')
            if (Array.isArray(parsed)) {
              setCorpEstrategiaPuntos(parsed)
            } else {
              setCorpEstrategiaPuntos(co.estrategia ? [{ id: crypto.randomUUID(), texto: co.estrategia }] : [])
            }
          } catch {
            setCorpEstrategiaPuntos(co.estrategia ? [{ id: crypto.randomUUID(), texto: co.estrategia }] : [])
          }
          const { data: mRows } = await supabase.from('strategic_objective_metrics').select('*').eq('strategic_objective_id', co.id)
          const mMap = {}
          ;(mRows ?? []).forEach(m => {
            mMap[`${m.year}_real`] = {
              ventas: m.ventas ?? '', ventas_unit: m.ventas_unit ?? 'USD millones',
              participacion_pct: m.participacion_pct ?? '',
              ventas_objetivo: m.ventas_objetivo ?? '', participacion_objetivo: m.participacion_objetivo ?? '',
              notes: m.notes ?? '',
            }
          })
          setCorpMetrics(mMap)
        }
      }
    }

    setLoading(false)
  }

  async function updatePhaseStatus(status) {
    await supabase.from('phases').update({ status }).eq('id', phaseId)
    setPhase(p => ({...p, status}))
  }

  // ── Excel import for Análisis del Mercado (batch version) ────────────────
  async function importMarketExcel(file) {
    setXlsxImporting(true)
    setXlsxResult(null)
    const details = []
    const norm  = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    const curYear = new Date().getFullYear()

    function sheetRows(sheetName) {
      const ws = wb.Sheets[sheetName] ?? wb.Sheets[wb.SheetNames.find(n => norm(n).includes(norm(sheetName).split(' ')[0]))]
      if (!ws) return []
      return XLSX.utils.sheet_to_json(ws, { defval: '' })
    }
    function parsePeriodo(val) {
      const s = String(val ?? '').trim().toUpperCase()
      if (s === 'Q1') return 1; if (s === 'Q2') return 2
      if (s === 'Q3') return 3; if (s === 'Q4') return 4
      return null
    }

    let wb
    try {
      const buf = await file.arrayBuffer()
      wb = XLSX.read(buf, { type: 'array' })

      // ── Find section IDs ─────────────────────────────────────────────────
      const { data: phaseSections, error: secErr } = await supabase
        .from('sections').select('id, name').eq('phase_id', phaseId)
      if (secErr) throw new Error(`Error cargando secciones: ${secErr.message}`)
      const phaseSecs = phaseSections ?? []
      const perfSec = phaseSecs.find(s => norm(s.name).includes('desempeno') && norm(s.name).includes('mercado'))
      const prodSec = phaseSecs.find(s => norm(s.name).includes('soluciones') || norm(s.name).includes('producto'))
      const compSec = phaseSecs.find(s => norm(s.name).includes('participacion'))
      const chanSec = phaseSecs.find(s => norm(s.name).includes('canal'))

      // ── Sheet 1: Desempeño del Mercado — batch delete + insert ───────────
      if (perfSec) {
        const rows = sheetRows('Desempeño del Mercado')
        const payloads = []
        for (const r of rows) {
          // Sheet 1 has no 'periodo' column — skip rows with no real data (header/warning rows)
          const year = parseInt(r['año'] ?? r['ano']) || null
          if (!year && r['tamaño_mercado'] === '' && r['volumen'] === '') continue
          if (!year) continue
          payloads.push({
            section_id: perfSec.id, data_year: year, data_quarter: null,
            market_size: r['tamaño_mercado'] !== '' ? parseFloat(r['tamaño_mercado']) || null : null,
            size_unit: r['unidad_ventas'] || 'USD millones',
            volume: r['volumen'] !== '' && r['volumen'] != null ? parseFloat(r['volumen']) || null : null,
            volume_unit: r['unidad_volumen'] || 'toneladas',
            reference_year: r['año_referencia'] ? parseInt(r['año_referencia']) : year,
            data_source: r['fuente_dato'] || null, maturity: r['madurez'] || null,
            has_seasonality: String(r['tiene_estacionalidad']).toLowerCase() === 'si',
            seasonality_months: r['meses_estacionalidad'] || null,
            updated_at: new Date().toISOString(),
          })
        }
        if (payloads.length > 0) {
          // Upsert each year's record individually (unique per section_id + data_year)
          let savedYears = []
          for (const payload of payloads) {
            const { data: existing } = await supabase.from('market_performance')
              .select('id').eq('section_id', perfSec.id).eq('data_year', payload.data_year).is('data_quarter', null).maybeSingle()
            if (existing) {
              const { error } = await supabase.from('market_performance').update(payload).eq('id', existing.id)
              if (error) throw new Error(`Desempeño del Mercado (${payload.data_year}): ${error.message}`)
            } else {
              const { error } = await supabase.from('market_performance').insert(payload)
              if (error) throw new Error(`Desempeño del Mercado (${payload.data_year}): ${error.message}`)
            }
            savedYears.push(payload.data_year)
          }
          details.push(`✅ Desempeño del Mercado: ${savedYears.length} año(s) guardado(s) — ${savedYears.join(', ')}`)
        } else { details.push('⚠️ Desempeño del Mercado: no se encontraron filas con año numérico válido') }
      } else { details.push('⚠️ No se encontró la sección "Desempeño del Mercado"') }

      // ── Sheet 2: Soluciones / Productos — batch insert, get IDs ──────────
      const productIdByKey = {}
      if (prodSec) {
        const rows = sheetRows('Soluciones (Productos)')
        const payloads = []; const seen = new Set()
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i]
          const quarter = parsePeriodo(r['periodo'] ?? r['período'])
          if (quarter === null) continue
          const year = parseInt(r['año'] ?? r['ano']) || curYear
          const name = String(r['nombre_producto'] || '').trim()
          if (!name) continue
          const key = `${name}|${year}|${quarter}`
          if (seen.has(key)) continue; seen.add(key)
          payloads.push({
            section_id: prodSec.id, data_year: year, data_quarter: quarter, name,
            sales_size: r['tamaño_ventas'] !== '' ? parseFloat(r['tamaño_ventas']) || 0 : 0,
            size_unit: r['unidad_ventas'] || 'USD millones',
            volume: r['volumen'] !== '' && r['volumen'] != null ? parseFloat(r['volumen']) || null : null,
            volume_unit: r['unidad_volumen'] || 'toneladas',
            reference_year: r['año_referencia'] ? parseInt(r['año_referencia']) : year,
            data_source: r['fuente_dato'] || null,
            description: String(r['descripcion'] || '') || null,
            product_order: i + 1,
          })
        }
        if (payloads.length > 0) {
          const years = [...new Set(payloads.map(p => p.data_year))]
          const quarters = [...new Set(payloads.map(p => p.data_quarter))]
          // Load existing to preserve IDs, delete only matching year/quarter combos
          const { data: existing } = await supabase.from('market_products')
            .select('id, name, data_year, data_quarter').eq('section_id', prodSec.id)
            .in('data_year', years).in('data_quarter', quarters)
          const existMap = {}
          ;(existing ?? []).forEach(p => { existMap[`${p.name}|${p.data_year}|${p.data_quarter}`] = p.id })
          const toInsert = payloads.filter(p => !existMap[`${p.name}|${p.data_year}|${p.data_quarter}`])
          const toUpdate = payloads.filter(p =>  existMap[`${p.name}|${p.data_year}|${p.data_quarter}`])
          // Batch insert new
          let inserted = []
          if (toInsert.length > 0) {
            const { data: ins, error } = await supabase.from('market_products').insert(toInsert).select('id, name, data_year, data_quarter')
            if (error) throw new Error(`Soluciones insert: ${error.message}`)
            inserted = ins ?? []
          }
          // Batch update existing (sequential but minimal)
          for (const p of toUpdate) {
            const id = existMap[`${p.name}|${p.data_year}|${p.data_quarter}`]
            await supabase.from('market_products').update(p).eq('id', id)
            productIdByKey[`${p.name}|${p.data_year}|${p.data_quarter}`] = id
          }
          inserted.forEach(p => { productIdByKey[`${p.name}|${p.data_year}|${p.data_quarter}`] = p.id })
          details.push(`✅ Soluciones (Productos): ${payloads.length} registro(s) importado(s)`)
        } else { details.push('⚠️ Soluciones (Productos): no se encontraron filas válidas') }
      } else { details.push('⚠️ No se encontró la sección "Soluciones (Productos)"') }

      // ── Sheet 3: Participaciones — batch insert competitors then shares ───
      if (compSec) {
        const rows = sheetRows('Participaciones (Competidores)')
        const validRows = rows.filter(r => parsePeriodo(r['periodo'] ?? r['período']) !== null && String(r['nombre_competidor'] || '').trim())
        const compPayloads = []; const compSeen = new Set()
        for (const r of validRows) {
          const quarter = parsePeriodo(r['periodo'] ?? r['período'])
          const year = parseInt(r['año'] ?? r['ano']) || curYear
          const name = String(r['nombre_competidor'] || '').trim()
          const key = `${name}|${year}|${quarter}`
          if (compSeen.has(key)) continue; compSeen.add(key)
          compPayloads.push({ section_id: compSec.id, data_year: year, data_quarter: quarter, name,
            description: String(r['descripcion'] || '') || null, competitor_order: compPayloads.length + 1 })
        }
        const compIdByKey = {}
        if (compPayloads.length > 0) {
          const years = [...new Set(compPayloads.map(p => p.data_year))]
          const quarters = [...new Set(compPayloads.map(p => p.data_quarter))]
          const { data: existing } = await supabase.from('market_competitors')
            .select('id, name, data_year, data_quarter').eq('section_id', compSec.id)
            .in('data_year', years).in('data_quarter', quarters)
          ;(existing ?? []).forEach(c => { compIdByKey[`${c.name}|${c.data_year}|${c.data_quarter}`] = c.id })
          const toInsert = compPayloads.filter(p => !compIdByKey[`${p.name}|${p.data_year}|${p.data_quarter}`])
          if (toInsert.length > 0) {
            const { data: ins, error } = await supabase.from('market_competitors').insert(toInsert).select('id, name, data_year, data_quarter')
            if (error) throw new Error(`Competidores: ${error.message}`)
            ;(ins ?? []).forEach(c => { compIdByKey[`${c.name}|${c.data_year}|${c.data_quarter}`] = c.id })
          }
        }
        // Build and batch insert shares
        const sharePayloads = []
        for (const r of validRows) {
          const quarter = parsePeriodo(r['periodo'] ?? r['período'])
          const year = parseInt(r['año'] ?? r['ano']) || curYear
          const compId = compIdByKey[`${String(r['nombre_competidor'] || '').trim()}|${year}|${quarter}`]
          const prodId = productIdByKey[`${String(r['nombre_producto'] || '').trim()}|${year}|${quarter}`]
          if (!compId || !prodId) continue
          const avgPrice = r['precio_promedio'] != null && r['precio_promedio'] !== '' ? parseFloat(r['precio_promedio']) : null
          sharePayloads.push({ competitor_id: compId, product_id: prodId,
            market_share: parseFloat(r['participacion_pct']) || 0,
            ...(avgPrice != null ? { avg_price: avgPrice } : {}),
            ...(r['unidad_precio'] ? { price_unit: String(r['unidad_precio']).trim() } : {}) })
        }
        if (sharePayloads.length > 0) {
          // Delete existing shares for these competitors then re-insert
          const compIds = [...new Set(sharePayloads.map(s => s.competitor_id))]
          await supabase.from('competitor_product_shares').delete().in('competitor_id', compIds)
          const { error } = await supabase.from('competitor_product_shares').insert(sharePayloads)
          if (error) throw new Error(`Participaciones (shares): ${error.message}`)
        }
        details.push(`✅ Participaciones: ${compPayloads.length} competidor(es) y ${sharePayloads.length} participacion(es) importada(s)`)
      } else { details.push('⚠️ No se encontró la sección "Participaciones"') }

      // ── Sheet 4: Canales — batch insert channels then weights ─────────────
      if (chanSec) {
        const rows = sheetRows('Canales')
        const validRows = rows.filter(r => parsePeriodo(r['periodo'] ?? r['período']) !== null && String(r['nombre_canal'] || '').trim())
        const chanPayloads = []; const chanSeen = new Set()
        for (const r of validRows) {
          const quarter = parsePeriodo(r['periodo'] ?? r['período'])
          const year = parseInt(r['año'] ?? r['ano']) || curYear
          const name = String(r['nombre_canal'] || '').trim()
          const key = `${name}|${year}|${quarter}`
          if (chanSeen.has(key)) continue; chanSeen.add(key)
          chanPayloads.push({ section_id: chanSec.id, data_year: year, data_quarter: quarter, name,
            description: String(r['descripcion'] || '') || null, channel_order: chanPayloads.length + 1,
            num_clientes: r['num_clientes'] !== '' && r['num_clientes'] != null ? parseInt(r['num_clientes']) || null : null,
            clientes_ponderado: r['clientes_ponderado'] !== '' && r['clientes_ponderado'] != null ? parseInt(r['clientes_ponderado']) || null : null })
        }
        const chanIdByKey = {}
        if (chanPayloads.length > 0) {
          const years = [...new Set(chanPayloads.map(p => p.data_year))]
          const quarters = [...new Set(chanPayloads.map(p => p.data_quarter))]
          const { data: existing } = await supabase.from('market_channels')
            .select('id, name, data_year, data_quarter').eq('section_id', chanSec.id)
            .in('data_year', years).in('data_quarter', quarters)
          ;(existing ?? []).forEach(c => { chanIdByKey[`${c.name}|${c.data_year}|${c.data_quarter}`] = c.id })
          const toInsert = chanPayloads.filter(p => !chanIdByKey[`${p.name}|${p.data_year}|${p.data_quarter}`])
          if (toInsert.length > 0) {
            const { data: ins, error } = await supabase.from('market_channels').insert(toInsert).select('id, name, data_year, data_quarter')
            if (error) throw new Error(`Canales: ${error.message}`)
            ;(ins ?? []).forEach(c => { chanIdByKey[`${c.name}|${c.data_year}|${c.data_quarter}`] = c.id })
          }
        }
        const weightPayloads = []
        for (const r of validRows) {
          const quarter = parsePeriodo(r['periodo'] ?? r['período'])
          const year = parseInt(r['año'] ?? r['ano']) || curYear
          const chanId = chanIdByKey[`${String(r['nombre_canal'] || '').trim()}|${year}|${quarter}`]
          const prodId = productIdByKey[`${String(r['nombre_producto'] || '').trim()}|${year}|${quarter}`]
          if (!chanId || !prodId) continue
          weightPayloads.push({ channel_id: chanId, product_id: prodId, weight: parseFloat(r['peso_pct']) || 0 })
        }
        if (weightPayloads.length > 0) {
          const chanIds = [...new Set(weightPayloads.map(w => w.channel_id))]
          await supabase.from('channel_product_weights').delete().in('channel_id', chanIds)
          const { error } = await supabase.from('channel_product_weights').insert(weightPayloads)
          if (error) throw new Error(`Pesos de canales: ${error.message}`)
        }
        details.push(`✅ Canales: ${chanPayloads.length} canal(es) y ${weightPayloads.length} peso(s) importado(s)`)
      } else { details.push('⚠️ No se encontró la sección "Canales"') }

      setXlsxResult({ ok: true, message: 'Importación completada exitosamente', details })
      fetchData()
    } catch (err) {
      console.error('Excel import error:', err)
      setXlsxResult({ ok: false, message: `Error al importar: ${err.message}`, details })
    }
    setXlsxImporting(false)
  }
  // ── End Excel import ──────────────────────────────────────────────────────

  // ── Generate market Excel template with Período column ────────────────────
  function generateMarketTemplate() {
    const wb = XLSX.utils.book_new()
    const curYear = new Date().getFullYear()

    // ── Hoja 0: Instrucciones ──────────────────────────────────────────────
    const instrucciones = [
      ['NEXT VALLEY CONSULTANTS  ·  Plantilla de Análisis de Mercado'],
      [''],
      ['INSTRUCCIONES DE USO'],
      [''],
      ['Esta plantilla contiene 4 hojas de datos. Complete cada una con la información del mercado que está analizando y luego importe el archivo directamente en la aplicación.'],
      [''],
      ['REGLA GENERAL (hojas 2, 3 y 4): Ingrese datos por TRIMESTRE (Q1, Q2, Q3, Q4). El total ANUAL lo calcula automáticamente la aplicación. No ingrese filas de totales anuales.'],
      ['HOJA 1 (Desempeño del Mercado): UNA fila por año — incluya datos históricos para ver tendencias (ej: 2022 a 2026).'],
      ['No modifique los nombres de las columnas (fila 1 de cada hoja) — son necesarios para la importación automática.'],
      [''],
      ['──────────────────────────────────────────────────────────────────────────────────────'],
      [''],
      ['HOJA 1: Desempeño del Mercado'],
      ['Qué captura:', 'El tamaño y volumen total del mercado por año. Ingrese una fila por año para ver tendencias.'],
      [''],
      ['Campo', 'Descripción', 'Ejemplo'],
      ['año', 'Año del dato (ej: 2022, 2023, 2024...)', '2024'],
      ['tamaño_mercado', 'Valor monetario del mercado ese año', '25'],
      ['unidad_ventas', 'Unidad de medida del valor (ej: USD millones, COP miles de millones)', 'USD millones'],
      ['volumen', 'Cantidad física vendida en el período', '125'],
      ['unidad_volumen', 'Unidad de medida del volumen (ej: toneladas, litros, unidades)', 'toneladas'],
      ['madurez', 'Etapa de madurez del mercado. Valores válidos exactos: Emergente, En crecimiento, Maduro, En declive', 'En crecimiento'],
      ['fuente_dato', 'Fuente de la información (ej: NielsenIQ, Euromonitor, estimación interna)', 'NielsenIQ'],
      ['tiene_estacionalidad', 'Si el mercado tiene estacionalidad marcada. Valores: Sí / No', 'No'],
      ['meses_estacionalidad', 'Si respondió Sí, indique los meses de mayor demanda (ej: Dic, Ene)', 'Dic, Ene'],
      [''],
      ['──────────────────────────────────────────────────────────────────────────────────────'],
      [''],
      ['HOJA 2: Soluciones (Productos)'],
      ['Qué captura:', 'Las categorías, subcategorías o productos que componen el mercado y su desempeño por período.'],
      [''],
      ['Campo', 'Descripción', 'Ejemplo'],
      ['año', 'Año al que corresponde el dato', '2024'],
      ['periodo', 'Trimestre del dato. Valores válidos: Q1, Q2, Q3, Q4', 'Q1'],
      ['nombre_producto', 'Nombre de la categoría, subcategoría o producto (sea consistente entre períodos)', 'Harina de trigo industrial'],
      ['tamaño_ventas', 'Valor monetario de ventas de este producto en el período', '15'],
      ['unidad_ventas', 'Unidad de medida del valor', 'USD millones'],
      ['volumen', 'Volumen físico de este producto en el período', '75'],
      ['unidad_volumen', 'Unidad de medida del volumen', 'toneladas'],
      ['descripcion', 'Descripción breve del producto (solo necesaria en la primera fila de cada producto)', 'Harina para panificación industrial de alta consistencia'],
      ['fuente_dato', 'Fuente de la información', 'NielsenIQ'],
      [''],
      ['──────────────────────────────────────────────────────────────────────────────────────'],
      [''],
      ['HOJA 3: Participaciones (Competidores)'],
      ['Qué captura:', 'La participación de mercado de cada competidor por producto y período, incluyendo precio promedio.'],
      [''],
      ['Campo', 'Descripción', 'Ejemplo'],
      ['año', 'Año al que corresponde el dato', '2024'],
      ['periodo', 'Trimestre del dato. Valores válidos: Q1, Q2, Q3, Q4', 'Q1'],
      ['nombre_competidor', 'Nombre del competidor o marca (sea consistente entre períodos)', 'Competidor A'],
      ['nombre_producto', 'Categoría o producto al que corresponde la participación', 'Harina de trigo industrial'],
      ['participacion_pct', 'Participación de mercado en porcentaje (0-100). No incluya el símbolo %', '35'],
      ['descripcion', 'Nota o descripción del competidor (solo necesaria en la primera fila de cada competidor)', 'Líder del mercado, fuerte en canal moderno'],
      ['precio_promedio', 'Precio promedio del competidor en el período', '4500'],
      ['unidad_precio', 'Unidad de precio (ej: USD/ton, USD/kg, COP/unidad)', 'USD/ton'],
      [''],
      ['Nota: La suma de participaciones por producto y período debería sumar 100%. La app validará esto al importar.'],
      [''],
      ['──────────────────────────────────────────────────────────────────────────────────────'],
      [''],
      ['HOJA 4: Canales'],
      ['Qué captura:', 'El peso de cada canal de distribución en el mercado por producto y período.'],
      [''],
      ['Campo', 'Descripción', 'Ejemplo'],
      ['año', 'Año al que corresponde el dato', '2024'],
      ['periodo', 'Trimestre del dato. Valores válidos: Q1, Q2, Q3, Q4', 'Q1'],
      ['nombre_canal', 'Nombre del canal (ej: Moderno, Tradicional, Food Service, E-commerce, Institucional)', 'Moderno'],
      ['nombre_producto', 'Categoría o producto al que corresponde el peso del canal', 'Harina de trigo industrial'],
      ['peso_pct', 'Participación del canal en las ventas totales del mercado, en porcentaje (0-100)', '58'],
      ['num_clientes', 'Número estimado de clientes activos en este canal (opcional)', '150'],
      ['clientes_ponderado', 'Clientes ponderados según tamaño o valor (opcional)', '30'],
      ['descripcion', 'Descripción o nota sobre el canal (solo primera fila de cada canal)', 'Supermercados y grandes superficies'],
      [''],
      ['Nota: La suma de pesos por producto y período debería sumar 100%. La app validará esto al importar.'],
      [''],
      ['──────────────────────────────────────────────────────────────────────────────────────'],
      [''],
      ['SOPORTE: Para dudas sobre el diligenciamiento contacte al equipo de Next Valley Consultants.'],
    ]
    const wsInstr = XLSX.utils.aoa_to_sheet(instrucciones)
    wsInstr['!cols'] = [{ wch: 28 }, { wch: 65 }, { wch: 35 }]
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instrucciones')

    // Sheet 1: Desempeño del Mercado — una fila por año
    const s1 = [
      ['año', 'tamaño_mercado', 'unidad_ventas', 'volumen', 'unidad_volumen', 'madurez', 'fuente_dato', 'tiene_estacionalidad', 'meses_estacionalidad'],
      ['⚠️ Una fila por año. Puedes incluir datos históricos (ej: 2022, 2023, 2024, 2025, 2026).', '', '', '', '', '', '', '', ''],
      [curYear - 4, '', 'USD millones', '', 'toneladas', 'En crecimiento', 'NielsenIQ', 'No', ''],
      [curYear - 3, '', 'USD millones', '', 'toneladas', 'En crecimiento', 'NielsenIQ', 'No', ''],
      [curYear - 2, '', 'USD millones', '', 'toneladas', 'En crecimiento', 'NielsenIQ', 'No', ''],
      [curYear - 1, '', 'USD millones', '', 'toneladas', 'En crecimiento', 'NielsenIQ', 'No', ''],
      [curYear,     '', 'USD millones', '', 'toneladas', 'En crecimiento', 'NielsenIQ', 'No', ''],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s1), 'Desempeño del Mercado')

    // Sheet 2: Soluciones (Productos)
    const s2 = [
      ['año', 'periodo', 'nombre_producto', 'tamaño_ventas', 'unidad_ventas', 'volumen', 'unidad_volumen', 'descripcion', 'fuente_dato'],
      ['', '⚠️ El total Anual lo calcula la app. Solo ingresa Q1, Q2, Q3, Q4.', '', '', '', '', '', '', ''],
      [curYear, 'Q1', 'Producto A', 15, 'USD millones', 75, 'toneladas', 'Descripción del producto', 'Fuente'],
      [curYear, 'Q2', 'Producto A', 15, 'USD millones', 75, 'toneladas', '', ''],
      [curYear, 'Q3', 'Producto A', 15, 'USD millones', 75, 'toneladas', '', ''],
      [curYear, 'Q4', 'Producto A', 15, 'USD millones', 75, 'toneladas', '', ''],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s2), 'Soluciones (Productos)')

    // Sheet 3: Participaciones (Competidores)
    const s3 = [
      ['año', 'periodo', 'nombre_competidor', 'nombre_producto', 'participacion_pct', 'descripcion', 'precio_promedio', 'unidad_precio'],
      ['', '⚠️ El promedio Anual lo calcula la app. Solo ingresa Q1, Q2, Q3, Q4.', '', '', '', '', '', ''],
      [curYear, 'Q1', 'Competidor A', 'Producto A', 35, 'Líder del mercado', 4500, 'USD/ton'],
      [curYear, 'Q2', 'Competidor A', 'Producto A', 33, '', '', ''],
      [curYear, 'Q3', 'Competidor A', 'Producto A', 36, '', '', ''],
      [curYear, 'Q4', 'Competidor A', 'Producto A', 35, '', '', ''],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s3), 'Participaciones (Competidores)')

    // Sheet 4: Canales
    const s4 = [
      ['año', 'periodo', 'nombre_canal', 'nombre_producto', 'peso_pct', 'num_clientes', 'clientes_ponderado', 'descripcion'],
      ['', '⚠️ El promedio Anual lo calcula la app. Solo ingresa Q1, Q2, Q3, Q4.', '', '', '', '', '', ''],
      [curYear, 'Q1', 'Moderno', 'Producto A', 58, 150, 30, 'Supermercados y grandes superficies'],
      [curYear, 'Q2', 'Moderno', 'Producto A', 60, '', '', ''],
      [curYear, 'Q3', 'Moderno', 'Producto A', 62, '', '', ''],
      [curYear, 'Q4', 'Moderno', 'Producto A', 60, '', '', ''],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s4), 'Canales')

    XLSX.writeFile(wb, 'plantilla_analisis_mercado.xlsx')
  }
  // ── End template generator ────────────────────────────────────────────────

  async function createSection(e) {
    e.preventDefault()
    setSavingSection(true)
    const nextOrder = sections.length + 1
    await supabase.from('sections').insert({
      phase_id: phaseId,
      name: sectionForm.name,
      description: sectionForm.description || null,
      assignee_id: sectionForm.assignee_id || null,
      section_order: nextOrder,
      status: 'pending',
    })
    setSectionOpen(false)
    setSectionForm({ name: '', description: '', assignee_id: '' })
    fetchData()
    setSavingSection(false)
  }

  function openEditSection(sec) {
    setEditSection(sec)
    setEditSectionForm({ name: sec.name, description: sec.description ?? '', assignee_id: sec.assignee_id ?? '' })
  }

  async function saveEditSection(e) {
    e.preventDefault()
    setSavingEdit(true)
    const payload = {
      name:        editSectionForm.name,
      description: editSectionForm.description || null,
      assignee_id: editSectionForm.assignee_id || null,
    }
    const { error } = await supabase.from('sections').update(payload).eq('id', editSection.id)
    if (error) {
      console.error('Error updating section:', error)
      alert(`Error al guardar: ${error.message}`)
      setSavingEdit(false)
      return
    }
    setSections(prev => prev.map(s => s.id === editSection.id ? { ...s, ...payload } : s))
    setEditSection(null)
    setSavingEdit(false)
  }

  async function deleteSection(sectionId) {
    if (!confirm('¿Eliminar esta sección y todas sus tareas?')) return
    await supabase.from('sections').delete().eq('id', sectionId)
    setSections(prev => prev.filter(s => s.id !== sectionId))
  }

  async function moveSection(index, direction) {
    const regularSecs = sections.filter(s => !s.consumer_profile_id && !s.competitor_id)
    const swapIndex = index + direction
    if (swapIndex < 0 || swapIndex >= regularSecs.length) return
    const updated = [...regularSecs]
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    const reordered = updated.map((s, i) => ({ ...s, section_order: i + 1 }))
    setSections(prev => [
      ...reordered,
      ...prev.filter(s => s.consumer_profile_id || s.competitor_id),
    ])
    await Promise.all([
      supabase.from('sections').update({ section_order: reordered[index].section_order }).eq('id', reordered[index].id),
      supabase.from('sections').update({ section_order: reordered[swapIndex].section_order }).eq('id', reordered[swapIndex].id),
    ])
  }

  // ── Consumer Profiles ──────────────────────────────────────
  const PROFILE_SUBSECTIONS = [
    'Necesidades y Tensiones',
    'Sobre la Solución',
    'Adopción de la Categoría',
    'Adopción de Marca y Nivel de Lealtad',
  ]

  async function createConsumerProfile(e) {
    e.preventDefault()
    setSavingProfile(true)
    const nextOrder = consumerProfiles.length + 1
    const { data: newProfile } = await supabase
      .from('consumer_profiles')
      .insert({ phase_id: phaseId, name: profileForm.name, description: profileForm.description || null, profile_order: nextOrder })
      .select()
      .single()

    if (newProfile) {
      // Crear las 4 sub-secciones estándar vinculadas al perfil
      await supabase.from('sections').insert(
        PROFILE_SUBSECTIONS.map((name, i) => ({
          phase_id:            phaseId,
          consumer_profile_id: newProfile.id,
          name,
          section_order:       i + 1,
          status:              'pending',
        }))
      )
      setProfileOpen(false)
      setProfileForm({ name: '', description: '' })
      fetchData()
    }
    setSavingProfile(false)
  }

  async function deleteConsumerProfile(profileId) {
    if (!confirm('¿Eliminar este perfil y todas sus secciones?')) return
    await supabase.from('consumer_profiles').delete().eq('id', profileId)
    setConsumerProfiles(prev => prev.filter(p => p.id !== profileId))
  }

  // ── Competitors ────────────────────────────────────────────
  const COMPETITOR_SUBSECTIONS = [
    'Posicionamiento',
    'Portafolio',
    'Canales',
    'Precio',
    'Capacidad Industrial',
    'Estrategia de Comunicación',
  ]

  // ── Bulk competitor template (Phase 3) ─────────────────────────────────
  // Lets the consultant download a 2-column Excel (Nombre + Descripción),
  // fill it offline with several competitors, and upload it. Each row →
  // one competitor inserted, with the 6 standard subsections auto-created.
  function downloadCompetitorTemplate() {
    const rows = [
      ['Nombre del Competidor (obligatorio)', 'Descripción (opcional)'],
      ['Ejemplo: Molinos del Sur', 'Líder regional en harinas para panificación industrial'],
      ['Ejemplo: Harinas ABC', 'Competidor mid-tier con foco en retail'],
    ]
    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = [{ wch: 36 }, { wch: 60 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Competidores')
    XLSX.writeFile(wb, 'plantilla_competidores.xlsx')
  }

  const [importingCompetitors, setImportingCompetitors] = useState(false)

  async function importCompetitorsExcel(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportingCompetitors(true)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
      // First row may be headers; skip rows that look like the example or
      // template instructions ("Nombre", "Ejemplo:")
      const competitorsToInsert = []
      for (const row of rows) {
        const name = String(row[0] ?? '').trim()
        const description = String(row[1] ?? '').trim()
        if (!name) continue
        const nameLc = name.toLowerCase()
        if (nameLc.includes('nombre') || nameLc.startsWith('ejemplo')) continue
        competitorsToInsert.push({ name, description: description || null })
      }
      if (!competitorsToInsert.length) {
        alert('No se encontraron competidores válidos en el Excel.')
        return
      }
      let nextOrder = competitors.length + 1
      let created = 0
      for (const c of competitorsToInsert) {
        const { data: newComp, error } = await supabase
          .from('competitors')
          .insert({ phase_id: phaseId, ...c, competitor_order: nextOrder })
          .select().single()
        if (error || !newComp) continue
        await supabase.from('sections').insert(
          COMPETITOR_SUBSECTIONS.map((name, i) => ({
            phase_id: phaseId, competitor_id: newComp.id,
            name, section_order: i + 1, status: 'pending',
          }))
        )
        nextOrder++
        created++
      }
      alert(`Se crearon ${created} competidor(es) con sus 6 subsecciones cada uno.`)
      fetchData()
    } catch (err) {
      console.error('[importCompetitorsExcel] error:', err)
      alert('Error al procesar el Excel: ' + (err.message ?? err))
    } finally {
      setImportingCompetitors(false)
      e.target.value = ''
    }
  }

  async function createCompetitor(e) {
    e.preventDefault()
    setSavingCompetitor(true)
    const nextOrder = competitors.length + 1
    const { data: newComp } = await supabase
      .from('competitors')
      .insert({
        phase_id:         phaseId,
        name:             competitorForm.name,
        description:      competitorForm.description || null,
        competitor_order: nextOrder,
      })
      .select()
      .single()

    if (newComp) {
      await supabase.from('sections').insert(
        COMPETITOR_SUBSECTIONS.map((name, i) => ({
          phase_id:      phaseId,
          competitor_id: newComp.id,
          name,
          section_order: i + 1,
          status:        'pending',
        }))
      )
      setCompetitorOpen(false)
      setCompetitorForm({ name: '', description: '' })
      fetchData()
    }
    setSavingCompetitor(false)
  }

  async function saveEditCompetitor(e) {
    e.preventDefault()
    setSavingCompetitor(true)
    await supabase.from('competitors').update({
      name:        editCompetitorForm.name,
      description: editCompetitorForm.description || null,
    }).eq('id', editCompetitor.id)
    setCompetitors(prev => prev.map(c =>
      c.id === editCompetitor.id ? { ...c, ...editCompetitorForm } : c
    ))
    setEditCompetitor(null)
    setSavingCompetitor(false)
  }

  async function deleteCompetitor(competitorId) {
    if (!confirm('¿Eliminar este competidor y todas sus secciones?')) return
    await supabase.from('competitors').delete().eq('id', competitorId)
    setCompetitors(prev => prev.filter(c => c.id !== competitorId))
  }

  async function duplicateSection() {
    if (!copySection || !copyTargetId) return
    setCopyingSection(true)
    try {
      // 1. Load all tasks from the source section
      const { data: sourceTasks } = await supabase
        .from('tasks')
        .select('name, description, priority, assignee_id, task_order')
        .eq('section_id', copySection.id)
        .order('task_order')

      // 2. Get current section count in target phase to set order
      const { count } = await supabase
        .from('sections')
        .select('id', { count: 'exact', head: true })
        .eq('phase_id', copyTargetId)

      // 3. Insert new section in target phase
      const isSamePhase = copyTargetId === phaseId
      const { data: newSection, error: secErr } = await supabase
        .from('sections')
        .insert({
          phase_id:      copyTargetId,
          name:          copySection.name + (isSamePhase ? ' (Copia)' : ''),
          description:   copySection.description || null,
          assignee_id:   copySection.assignee_id || null,
          section_order: (count ?? 0) + 1,
          status:        'pending',
        })
        .select()
        .single()
      if (secErr || !newSection) throw new Error('Error creando sección')

      // 4. Insert task copies (status reset to pending)
      if (sourceTasks?.length) {
        await supabase.from('tasks').insert(
          sourceTasks.map(t => ({
            section_id:  newSection.id,
            name:        t.name,
            description: t.description || null,
            priority:    t.priority    || null,
            assignee_id: t.assignee_id || null,
            task_order:  t.task_order,
            status:      'pending',
          }))
        )
      }

      setCopyDone(true)
      setTimeout(() => {
        setCopySection(null)
        setCopyTargetId('')
        setCopyDone(false)
        if (isSamePhase) fetchData()
      }, 1400)
    } catch (err) {
      console.error('Error duplicando sección:', err)
      alert('Error al duplicar la sección')
    } finally {
      setCopyingSection(false)
    }
  }

  async function deleteReport(reportId) {
    if (!window.confirm('¿Eliminar este informe? Esta acción no se puede deshacer.')) return
    await supabase.from('ai_reports').delete().eq('id', reportId)
    setAiReports(prev => prev.filter(r => r.id !== reportId))
  }

  // Converts markdown lines into docx Paragraph objects
  function markdownToDocxParagraphs(markdown) {
    const paras = []
    for (const line of markdown.split('\n')) {
      const h3 = line.match(/^### (.+)/)
      const h2 = line.match(/^## (.+)/)
      const h1 = line.match(/^# (.+)/)
      const bullet = line.match(/^[-*] (.+)/)
      const clean = line.replace(/\*\*/g, '').replace(/^[-*#\s]+/, '').trim()
      if (!clean) { paras.push(new Paragraph({ text: '' })); continue }
      if (h1) {
        paras.push(new Paragraph({ text: h1[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_1, spacing: { before: 320, after: 120 } }))
      } else if (h2) {
        paras.push(new Paragraph({ text: h2[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_2, spacing: { before: 240, after: 80 } }))
      } else if (h3) {
        paras.push(new Paragraph({ text: h3[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_3, spacing: { before: 160, after: 60 } }))
      } else if (bullet) {
        paras.push(new Paragraph({ children: [new TextRun({ text: bullet[1].replace(/\*\*/g, ''), size: 22 })], bullet: { level: 0 }, spacing: { after: 40 } }))
      } else {
        // Inline bold: split on **...**
        const parts = line.split(/(\*\*[^*]+\*\*)/)
        const runs = parts.map(p => {
          if (p.startsWith('**') && p.endsWith('**')) return new TextRun({ text: p.slice(2, -2), bold: true, size: 22 })
          return new TextRun({ text: p, size: 22 })
        })
        paras.push(new Paragraph({ children: runs, spacing: { after: 60 } }))
      }
    }
    return paras
  }

  async function exportReportAsWord(report) {
    const phaseName = phase?.name || 'Informe'
    const companyName = ''   // not in scope here — just use phase name
    const date = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })

    const children = [
      new Paragraph({
        children: [new TextRun({ text: phaseName.toUpperCase(), bold: true, size: 36, color: '1a3f6b' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
      }),
      new Paragraph({
        children: [new TextRun({ text: 'Informe del Agente Estratégico — Next Valley Consultants', size: 22, color: '6b7280', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `Generado el ${date}`, size: 20, color: '9ca3af' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 480 },
      }),
      ...markdownToDocxParagraphs(report.content),
      new Paragraph({
        children: [new TextRun({ text: `Documento generado el ${date} mediante el Agente Estratégico NVC`, size: 16, color: '9ca3af', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { before: 600 },
      }),
    ]

    const doc = new Document({
      sections: [{ properties: {}, children }],
      styles: { default: { document: { run: { font: 'Calibri', size: 22 } } } },
    })

    const blob = await Packer.toBlob(doc)
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${phaseName.toLowerCase().replace(/\s+/g, '-')}-informe.docx`
    a.click()
  }

  function exportReportAsPDF(report) {
    import('jspdf').then(({ jsPDF }) => {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' })
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 20
      const maxW = pageW - margin * 2
      const phaseName = phase?.name || 'Informe'
      const date = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })

      // Header
      doc.setFillColor(31, 41, 91)
      doc.rect(0, 0, pageW, 32, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text('NEXT VALLEY CONSULTANTS', margin, 11)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(phaseName, margin, 22)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(date, pageW - margin, 22, { align: 'right' })

      // Content — strip markdown symbols for plain rendering
      doc.setTextColor(30, 30, 30)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      const plainText = report.content
        .replace(/^#{1,3} /gm, '')
        .replace(/\*\*/g, '')
        .replace(/^[-*] /gm, '• ')
      const lines = doc.splitTextToSize(plainText, maxW)
      let y = 46

      for (const line of lines) {
        if (y > pageH - 18) { doc.addPage(); y = 20 }
        // Bold for heading-like lines (all caps or starts with •)
        const isHeading = line === line.toUpperCase() && line.trim().length > 3
        doc.setFont('helvetica', isHeading ? 'bold' : 'normal')
        doc.text(line, margin, y)
        y += 5.5
      }

      // Footer
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(150)
        doc.text(`Página ${i} de ${totalPages} — Next Valley Consultants`, pageW / 2, pageH - 8, { align: 'center' })
      }

      doc.save(`${phaseName.toLowerCase().replace(/\s+/g, '-')}-informe.pdf`)
    })
  }

  async function saveUserNotes() {
    if (!userNotes.trim()) return
    setSavingNotes(true)
    if (notesRecordId) {
      await supabase.from('ai_reports').update({ content: userNotes }).eq('id', notesRecordId)
    } else {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: id,
        entity_type: 'phase',
        entity_id: phaseId,
        report_type: 'user_notes',
        content: userNotes,
      }).select().single()
      if (data) setNotesRecordId(data.id)
    }
    setSavedNotes(userNotes)
    setEditingNotes(false)
    setSavingNotes(false)
  }

  async function saveResult() {
    if (!resultText.trim()) return
    setSavingResult(true)
    if (resultRecordId) {
      await supabase.from('ai_reports').update({ content: resultText }).eq('id', resultRecordId)
    } else {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: id,
        entity_type: 'phase',
        entity_id: phaseId,
        report_type: 'resultado',
        content: resultText,
      }).select().single()
      if (data) setResultRecordId(data.id)
    }
    setSavedResult(resultText)
    setEditingResult(false)
    setSavingResult(false)
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )
  if (!phase) return <div className="text-center py-20 text-gray-400">Fase no encontrada</div>

  const normPhaseName = (phase?.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const isMarketPhase  = normPhaseName.includes('mercado')
  const isCorpInternalPhase = !phase?.business_unit_id && normPhaseName.includes('interno')
  const isEtapa0 = normPhaseName.includes('modelo de negocio') || normPhaseName.includes('entendimiento') || (phase?.phase_order === 0)

  const tabs = [
    ...(!isEtapa0 ? [{ id: 'sections', label: 'Secciones', icon: BarChart3, special: false }] : []),
    ...(isEtapa0 ? [
      { id: 'mi_modelo',    label: 'Mi Modelo de Negocio',  icon: Lightbulb,     special: true  },
      { id: 'drl',          label: 'Información necesaria',  icon: ClipboardList, special: false },
      { id: 'stakeholders', label: 'Stakeholders',          icon: UserCheck,     special: false },
      { id: 'afirmaciones', label: 'Afirmaciones',          icon: Quote,         special: false },
    ] : []),
    ...(isCorpInternalPhase ? [{ id: 'objetivo_corp', label: 'Objetivo Estratégico', icon: Target, special: false }] : []),
    { id: 'resultado',     label: 'Resultado',              icon: Trophy,        special: true  },
    { id: 'informe',       label: 'Informe IA',             icon: Sparkles,      special: false },
    { id: 'files',         label: 'Archivos',               icon: FileText,      special: false },
    { id: 'comments',      label: 'Comentarios',            icon: MessageSquare, special: false },
  ]

  return (
    <div className="space-y-6">
      {/* Banner DEMO si esta consultancy es la ejemplo */}
      {isDemo && <DemoBanner onSubscribe={() => navigate('/caja')} />}

      {/* Hidden file input for Excel import */}
      <input
        ref={xlsxInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) importMarketExcel(f)
          e.target.value = ''
        }}
      />

      {/* Breadcrumb — labeled with entity type per crumb. Current location
          (Etapa) gets a colored chip so the user always knows the depth. */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 flex-wrap">
        <button onClick={() => navigate(`/consultancy/${id}`)}
          className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors">
          <Building2 size={13} className="opacity-60" />
          <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">Proyecto:</span>
          <span>{consultancyName || 'Consultoría'}</span>
        </button>
        {!isProjectPhase && (
          <>
            <ChevronRight size={14} className="flex-shrink-0" />
            <button onClick={() => navigate(`/consultancy/${id}/bu/${buId}`)}
              className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors truncate max-w-[180px] md:max-w-none">
              <Target size={13} className="opacity-60" />
              <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">UN:</span>
              <span className="truncate">{buName || 'Unidad de Negocio'}</span>
            </button>
          </>
        )}
        <ChevronRight size={14} className="flex-shrink-0" />
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 font-semibold truncate max-w-[220px] md:max-w-none">
          <BarChart2 size={13} />
          <span className="text-[10px] uppercase tracking-wide">Etapa:</span>
          <span className="truncate">{phase.name}</span>
        </span>
      </div>

      {/* Caja phase guidance banner */}
      {isCaja && (() => {
        const meta = CAJA_PHASE_META[phase?.phase_order]
        if (!meta) return null
        return (
          <div className={`rounded-2xl bg-gradient-to-r ${meta.color} px-5 py-4 text-white`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none flex-shrink-0 mt-0.5">{meta.icon}</span>
              <div>
                <p className="text-sm font-semibold leading-snug mb-1">{meta.description}</p>
                <p className="text-xs opacity-75 leading-relaxed">💡 {meta.tip}</p>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-gray-900">{phase.name}</h1>
              <StatusBadge status={phase.status} />
            </div>
            {phase.description && <p className="text-sm text-gray-500 mt-1">{phase.description}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {/* Excel import button — only for Análisis del Mercado phases */}
            {isMarketPhase && isNVC && (
              <button
                onClick={() => xlsxInputRef.current?.click()}
                disabled={xlsxImporting}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-60"
                title="Importar datos de las 4 secciones desde un Excel"
              >
                {xlsxImporting
                  ? <><Loader size={13} className="animate-spin" /> Importando…</>
                  : <><Upload size={13} /> Importar Excel</>
                }
              </button>
            )}
            {!isCaja && (
              <AIReportButton consultancyId={id} entityType="phase" entityId={phaseId} label="Informe de Etapa" />
            )}
            {(isAdmin || isNVC) && phase && (
              <button
                onClick={async () => {
                  setLockingPhase(true)
                  const newLocked = !phaseLocked
                  await supabase.from('phases').update({
                    is_locked: newLocked,
                    locked_by: newLocked ? (profile?.id ?? null) : null,
                    locked_at: newLocked ? new Date().toISOString() : null,
                  }).eq('id', phaseId)
                  setPhaseLocked(newLocked)
                  setLockingPhase(false)
                }}
                disabled={lockingPhase}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 ${
                  phaseLocked
                    ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
                title={phaseLocked ? 'Fase bloqueada — clic para desbloquear' : 'Bloquear fase'}
              >
                {lockingPhase
                  ? <><Loader size={12} className="animate-spin" />{phaseLocked ? 'Desbloqueando...' : 'Bloqueando...'}</>
                  : phaseLocked
                    ? <><Lock size={12} /> Bloqueada</>
                    : <><Unlock size={12} /> Bloquear</>}
              </button>
            )}
          </div>
        </div>

        {/* Excel import result banner */}
        {xlsxResult && (
          <div className={`mt-3 rounded-xl border p-3 text-sm ${xlsxResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <div className="flex items-start gap-2">
              {xlsxResult.ok
                ? <CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                : <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              }
              <div className="flex-1">
                <p className="font-medium">{xlsxResult.message}</p>
                {xlsxResult.details?.map((d, i) => (
                  <p key={i} className="text-xs mt-0.5 opacity-80">{d}</p>
                ))}
              </div>
              <button onClick={() => setXlsxResult(null)} className="text-current opacity-50 hover:opacity-80 flex-shrink-0">
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Status selector */}
        <div className="mt-4 flex gap-2 flex-wrap">
          {['pending','in_progress','review','completed','blocked'].map(s => (
            <button key={s} onClick={() => updatePhaseStatus(s)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                phase.status === s
                  ? 'bg-brand-700 text-white border-brand-700'
                  : 'border-gray-200 text-gray-500 hover:border-brand-300'
              }`}>
              {s === 'pending' ? 'Pendiente' : s === 'in_progress' ? 'En Progreso' : s === 'review' ? 'En Revisión' : s === 'completed' ? 'Completado' : 'Bloqueado'}
            </button>
          ))}
        </div>

        {/* Download template link (only for market phases) */}
        {isMarketPhase && isNVC && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
            <Download size={12} />
            <span>¿No tienes la plantilla? </span>
            <button
              onClick={generateMarketTemplate}
              className="text-brand-600 hover:underline"
            >
              Descarga aquí la plantilla Excel
            </button>
          </div>
        )}

        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progreso de la etapa</span><span>{phase.progress ?? 0}%</span>
          </div>
          <ProgressBar value={phase.progress ?? 0} showLabel={false} />
        </div>
      </div>

      {/* Lock banner */}
      {phaseLocked && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
          <Lock size={15} className="flex-shrink-0 text-amber-600" />
          Esta fase está bloqueada — solo lectura. Solo NVC/Admin puede desbloquearla.
        </div>
      )}

      {/* DRL progress — solo Etapa 0, para verlo sin entrar al tab */}
      {isEtapa0 && (
        <DRLProgressBars consultancyId={id} />
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 flex-wrap">
        {tabs.map(({ id: tid, label, icon: Icon, special }) => (
          special ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                  : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
              }`}>
              <Icon size={15} /> {label}
              {savedResult && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
            </button>
          ) : (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tid ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={15} /> {label}
            </button>
          )
        ))}
      </div>

      {activeTab === 'sections' && (() => {
        const regularSections = sections.filter(s => !s.consumer_profile_id && !s.competitor_id)
        const isConsumerPhase    = phase?.name?.toLowerCase().includes('consumidor')
        const isCompetitivePhase = phase?.name?.toLowerCase().includes('competitiv')
        return (
        <div className="space-y-3">
          {/* Add section button */}
          {isNVC && (
            <button onClick={() => setSectionOpen(true)}
              className="btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> Nueva Sección
            </button>
          )}

          {/* Regular sections */}
          {regularSections.map((section, idx) => {
            const completedTasks = section.tasks?.filter(t => t.status === 'completed').length ?? 0
            const totalTasks     = section.tasks?.length ?? 0
            return (
              <div key={section.id} className="card p-4 hover:shadow-md hover:border-brand-300 transition-all group">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(
                      isProjectPhase
                        ? `/consultancy/${id}/phase/${phaseId}/section/${section.id}`
                        : `/consultancy/${id}/bu/${buId}/phase/${phaseId}/section/${section.id}`
                    )}
                    className="flex items-center gap-4 flex-1 text-left min-w-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-sm text-gray-900 group-hover:text-brand-700 transition-colors">
                          {section.name}
                        </span>
                        <StatusBadge status={section.status} />
                      </div>
                      {section.description && (
                        <p className="text-xs text-gray-400 mb-1 line-clamp-1">{section.description}</p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-gray-400">{completedTasks}/{totalTasks} tareas completadas</span>
                        {(section.data_pct > 0) && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                            section.data_pct >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : section.data_pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                          }`}>
                            {section.data_pct}% datos
                          </span>
                        )}
                        {section.section_assignee && (
                          <span className="flex items-center gap-1 text-xs text-brand-600">
                            <User size={10} /> {section.section_assignee.full_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-gray-300 group-hover:text-brand-400 flex-shrink-0" />
                  </button>

                  {(isNVC || isAdmin) && (
                    <div className="flex gap-1 flex-shrink-0">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                          className="p-1 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Subir sección"><ChevronUp size={14} /></button>
                        <button onClick={() => moveSection(idx, 1)} disabled={idx === regularSections.length - 1}
                          className="p-1 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Bajar sección"><ChevronDown size={14} /></button>
                      </div>
                      {isAdmin && (
                        <button onClick={() => openEditSection(section)}
                          className="p-2 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition-colors"
                          title="Editar sección"><Pencil size={14} /></button>
                      )}
                      <button onClick={() => { setCopySection(section); setCopyTargetId(''); setCopyDone(false) }}
                        className="p-2 text-gray-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Duplicar sección a otra fase"><Copy size={14} /></button>
                      {isAdmin && (
                        <button onClick={() => deleteSection(section.id)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar sección"><Trash2 size={14} /></button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {regularSections.length === 0 && !isConsumerPhase && !isCompetitivePhase && (
            <div className="text-center py-10 text-gray-400">
              <BarChart3 size={40} className="mx-auto mb-2 opacity-20" />
              <p>No hay secciones en esta etapa</p>
              {isNVC && <p className="text-sm mt-1">Crea la primera con el botón de arriba</p>}
            </div>
          )}

          {/* ── Competidores (solo en etapas de Análisis Competitivo) ── */}
          {isCompetitivePhase && (
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Swords size={14} className="text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Competidores</h3>
                  {competitors.length > 0 && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                      {competitors.length}
                    </span>
                  )}
                </div>
                {(isNVC || isAdmin) && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={downloadCompetitorTemplate}
                      title="Descargar plantilla Excel para subir competidores en bulk"
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors">
                      <Download size={13} /> Plantilla
                    </button>
                    <label className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors cursor-pointer"
                      title="Subir competidores desde Excel — cada fila se vuelve un competidor con sus 6 subsecciones">
                      {importingCompetitors
                        ? <><Loader size={13} className="animate-spin" /> Importando…</>
                        : <><Upload size={13} /> Subir Excel</>}
                      <input type="file" accept=".xlsx,.xls" className="hidden"
                        disabled={importingCompetitors}
                        onChange={importCompetitorsExcel} />
                    </label>
                    <button onClick={() => setCompetitorOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors">
                      <Plus size={13} /> Nuevo Competidor
                    </button>
                  </div>
                )}
              </div>

              {competitors.map(comp => (
                <CompetitorCard
                  key={comp.id}
                  competitor={comp}
                  navigate={navigate}
                  consultancyId={id}
                  buId={buId}
                  phaseId={phaseId}
                  isProjectPhase={isProjectPhase}
                  isAdmin={isAdmin}
                  isNVC={isNVC}
                  onEdit={() => { setEditCompetitor(comp); setEditCompetitorForm({ name: comp.name, description: comp.description ?? '' }) }}
                  onDelete={() => deleteCompetitor(comp.id)}
                />
              ))}

              {competitors.length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400">
                  <Swords size={36} className="mx-auto mb-2 opacity-20" />
                  <p className="text-sm font-medium">No hay competidores cargados aún</p>
                  {(isNVC || isAdmin) && <p className="text-xs mt-1">Agrega el primero con el botón de arriba</p>}
                </div>
              )}
            </div>
          )}

          {/* ── Perfiles de Consumidor (solo en etapas de Análisis de Consumidor) ── */}
          {isConsumerPhase && (
            <div className="mt-6 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Users size={14} className="text-violet-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Perfiles de Consumidor</h3>
                  {consumerProfiles.length > 0 && (
                    <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                      {consumerProfiles.length}
                    </span>
                  )}
                </div>
                {isNVC && (
                  <button onClick={() => setProfileOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors">
                    <Plus size={13} /> Nuevo Perfil
                  </button>
                )}
              </div>

              {/* Profile cards */}
              {consumerProfiles.map(profile => (
                <ConsumerProfileCard
                  key={profile.id}
                  profile={profile}
                  navigate={navigate}
                  consultancyId={id}
                  buId={buId}
                  phaseId={phaseId}
                  isProjectPhase={isProjectPhase}
                  isAdmin={isAdmin}
                  onDelete={() => deleteConsumerProfile(profile.id)}
                />
              ))}

              {consumerProfiles.length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400">
                  <Users size={36} className="mx-auto mb-2 opacity-20" />
                  <p className="text-sm font-medium">No hay perfiles de consumidor aún</p>
                  {isNVC && <p className="text-xs mt-1">Crea el primer perfil con el botón de arriba</p>}
                </div>
              )}
            </div>
          )}
        </div>
        )
      })()}

      {activeTab === 'mi_modelo' && isEtapa0 && (
        <MiModeloNegocio consultancyId={id} />
      )}

      {activeTab === 'drl' && isEtapa0 && (
        isCaja
          ? <CajaDRL
              consultancyId={id}
              isTrial={profile?.subscription_status === 'trial' || !profile?.subscription_status}
              onLimitReached={() => {
                // Cuando alcanza el límite de 5 archivos, le mostramos un alert
                // y le decimos cómo desbloquear. Idealmente abriríamos paywall
                // pero el banner del dashboard ya lo cumple — aquí solo avisamos.
                alert('Has alcanzado el límite de 5 archivos en modo trial. Suscríbete desde el dashboard para subir más archivos.')
              }}
            />
          : <div className="card"><DRLTab consultancyId={id} /></div>
      )}

      {activeTab === 'stakeholders' && isEtapa0 && (
        <div className="card">
          <StakeholdersTab consultancyId={id} />
        </div>
      )}

      {activeTab === 'afirmaciones' && isEtapa0 && (
        <div className="card">
          <AfirmacionesTab consultancyId={id} phaseId={phaseId} />
        </div>
      )}

      {activeTab === 'informe' && (
        <div className="space-y-6">
          {/* AI-generated reports */}
          {aiReports.length > 0 ? (
            aiReports.map((report, idx) => (
              <div key={report.id} className="card border border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white overflow-hidden">
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-indigo-100">
                  <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
                    <Sparkles size={14} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {aiReports.length > 1 ? `Informe generado por el Agente #${aiReports.length - idx}` : 'Informe generado por el Agente'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(report.created_at).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => exportReportAsWord(report)}
                      title="Exportar como Word (.docx)"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    >
                      <FileText size={14} />
                    </button>
                    <button
                      onClick={() => exportReportAsPDF(report)}
                      title="Exportar como PDF"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    >
                      <Download size={14} />
                    </button>
                    {(isAdmin || isNVC) && (
                      <button
                        onClick={() => deleteReport(report.id)}
                        title="Eliminar informe"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="prose prose-sm max-w-none text-gray-700
                  prose-headings:text-gray-900 prose-headings:font-semibold
                  prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                  prose-strong:text-gray-800 prose-li:my-0.5 prose-p:my-1.5
                  prose-table:text-xs prose-th:bg-gray-50 prose-th:p-2 prose-td:p-2 prose-table:border prose-td:border prose-th:border">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {report.content}
                  </ReactMarkdown>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10 text-gray-400">
              <Sparkles size={40} className="mx-auto mb-2 opacity-20" />
              <p className="font-medium">Sin informes del agente aún</p>
              <p className="text-sm mt-1">Cuando el Agente Estratégico complete esta fase, el informe aparecerá aquí.</p>
            </div>
          )}

          {/* User notes section */}
          {(isAdmin || isNVC) && (
            <div className="card border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Edit3 size={16} className="text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Notas y ajustes del consultor</h3>
                </div>
                {!editingNotes && (
                  <button
                    onClick={() => setEditingNotes(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-brand-300 hover:text-brand-600 transition-colors"
                  >
                    <Pencil size={12} /> {savedNotes ? 'Editar notas' : 'Agregar notas'}
                  </button>
                )}
              </div>

              {editingNotes ? (
                <div className="space-y-3">
                  <textarea
                    className="input resize-none text-sm"
                    rows={8}
                    value={userNotes}
                    onChange={e => setUserNotes(e.target.value)}
                    placeholder="Escribe aquí tus notas, ajustes o información adicional para esta etapa. Soporta Markdown (negrita, listas, tablas)..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setUserNotes(savedNotes); setEditingNotes(false) }}
                      className="btn-secondary text-sm flex-1"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveUserNotes}
                      disabled={savingNotes || !userNotes.trim()}
                      className="btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                    >
                      <Save size={14} />
                      {savingNotes ? 'Guardando...' : 'Guardar notas'}
                    </button>
                  </div>
                </div>
              ) : savedNotes ? (
                <div className="prose prose-sm max-w-none text-gray-700
                  prose-headings:text-gray-900 prose-headings:font-semibold
                  prose-h2:text-base prose-h3:text-sm
                  prose-strong:text-gray-800 prose-li:my-0.5 prose-p:my-1.5">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {savedNotes}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Sin notas agregadas aún. Haz clic en "Agregar notas" para enriquecer este informe.</p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'resultado' && (
        <div className="space-y-5">
          {/* Header banner */}
          <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Trophy size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">Resultado de la Etapa</h2>
              <p className="text-emerald-100 text-xs mt-0.5">Documento final y análisis consolidado de esta etapa</p>
            </div>
          </div>

          {/* Analysis text */}
          <div className="card border border-emerald-100 bg-emerald-50/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Edit3 size={15} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-800">Análisis y conclusiones</h3>
              </div>
              {!editingResult && (isAdmin || isNVC) && (
                <button
                  onClick={() => setEditingResult(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  <Pencil size={12} /> {savedResult ? 'Editar' : 'Agregar análisis'}
                </button>
              )}
            </div>

            {editingResult ? (
              <div className="space-y-3">
                <textarea
                  className="input resize-none text-sm"
                  rows={10}
                  value={resultText}
                  onChange={e => setResultText(e.target.value)}
                  placeholder="Escribe aquí el análisis, hallazgos y conclusiones de esta etapa. Soporta Markdown (negrita, listas, tablas)..."
                />
                <div className="flex gap-2">
                  <button onClick={() => { setResultText(savedResult); setEditingResult(false) }} className="btn-secondary text-sm flex-1">
                    Cancelar
                  </button>
                  <button
                    onClick={saveResult}
                    disabled={savingResult || !resultText.trim()}
                    className="text-sm flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    <Save size={14} />
                    {savingResult ? 'Guardando...' : 'Guardar resultado'}
                  </button>
                </div>
              </div>
            ) : savedResult ? (
              <div className="prose prose-sm max-w-none text-gray-700
                prose-headings:text-gray-900 prose-headings:font-semibold
                prose-h2:text-base prose-h3:text-sm
                prose-strong:text-gray-800 prose-li:my-0.5 prose-p:my-1.5">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedResult}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Sin análisis agregado aún.{(isAdmin || isNVC) ? ' Haz clic en "Agregar análisis" para documentar el resultado.' : ''}</p>
            )}
          </div>

          {/* Result documents */}
          <div className="card border border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={15} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">Documentos del resultado</h3>
            </div>
            <FileUpload entityType="resultado_phase" entityId={phaseId} onUploaded={() => setResultFileKey(k => k + 1)} />
            <div className="mt-4">
              <FileList entityType="resultado_phase" entityId={phaseId} refreshKey={resultFileKey} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="space-y-4">
          <FileUpload entityType="phase" entityId={phaseId} onUploaded={() => {}} />
          <FileList entityType="phase" entityId={phaseId} />
        </div>
      )}

      {activeTab === 'comments' && (
        <Comments entityType="phase" entityId={phaseId} />
      )}

      {/* ── Objetivo Estratégico Corporativo (project-level internal analysis) ── */}
      {activeTab === 'objetivo_corp' && (() => {
        function updateMetric(year, field, value) {
          setCorpMetrics(prev => ({ ...prev, [`${year}_real`]: { ...(prev[`${year}_real`] ?? {}), [field]: value } }))
        }
        function getMetric(year, field) {
          return corpMetrics[`${year}_real`]?.[field] ?? ''
        }
        function cumplimiento(year) {
          const m   = corpMetrics[`${year}_real`] ?? {}
          const real = parseFloat(m.ventas)
          const obj  = parseFloat(m.ventas_objetivo)
          if (!real || !obj || obj === 0) return null
          return Math.round((real / obj) * 100)
        }

        async function saveCorpObj() {
          setSavingCorpObj(true)
          const payload = {
            phase_id:       phaseId,
            consultancy_id: id,   // ← required for RLS access via consultancy
            vision:         corpObjForm.vision || null,
            mision:         corpObjForm.mision || null,
            declaracion:    corpObjForm.declaracion || null,
            estrategia:     corpEstrategiaPuntos.length > 0 ? JSON.stringify(corpEstrategiaPuntos) : null,
            generated_text: corpObjForm.generated_text || null,
            updated_at:     new Date().toISOString(),
          }
          let coId = corpObj?.id
          if (corpObj) {
            await supabase.from('strategic_objectives').update(payload).eq('id', corpObj.id)
          } else {
            const { data: newCo } = await supabase.from('strategic_objectives').insert(payload).select().single()
            if (newCo) { setCorpObj(newCo); coId = newCo.id }
          }
          if (coId) {
            await supabase.from('strategic_objective_metrics').delete().eq('strategic_objective_id', coId)
            const inserts = CORP_REAL_YEARS.map(year => {
              const m = corpMetrics[`${year}_real`] ?? {}
              return {
                strategic_objective_id: coId, year, type: 'real',
                ventas:                 m.ventas !== '' ? Number(m.ventas) : null,
                ventas_unit:            m.ventas_unit || 'USD millones',
                participacion_pct:      m.participacion_pct !== '' ? Number(m.participacion_pct) : null,
                ventas_objetivo:        m.ventas_objetivo !== '' ? Number(m.ventas_objetivo) : null,
                participacion_objetivo: m.participacion_objetivo !== '' ? Number(m.participacion_objetivo) : null,
                notes:                  m.notes || null,
              }
            }).filter(r => r.ventas != null || r.ventas_objetivo != null || r.participacion_pct != null)
            if (inserts.length > 0) await supabase.from('strategic_objective_metrics').insert(inserts)
          }
          setSavingCorpObj(false)
          setCorpObjSaved(true)
          setTimeout(() => setCorpObjSaved(false), 2500)
        }

        async function generateCorpObj() {
          setGeneratingCorpObj(true)
          const realRows = CORP_REAL_YEARS.map(y => {
            const m = corpMetrics[`${y}_real`] ?? {}
            const c = cumplimiento(y)
            return `  ${y}: Real ${m.ventas || '—'} ${m.ventas_unit || ''} | Obj.pasado ${m.ventas_objetivo || '—'} | Cumpl. ${c != null ? c + '%' : '—'} | Part. ${m.participacion_pct || '—'}%`
          }).join('\n')
          const estrategiaText = corpEstrategiaPuntos.length > 0
            ? corpEstrategiaPuntos.map((p, i) => `  ${i + 1}. ${p.texto}`).join('\n')
            : '(no definida)'
          const context = `VISIÓN:\n${corpObjForm.vision || '(no definida)'}\n\nMISIÓN:\n${corpObjForm.mision || '(no definida)'}\n\nDECLARACIÓN DEL OBJETIVO:\n${corpObjForm.declaracion || '(no definida)'}\n\nESTRATEGIA PARA LOGRARLO:\n${estrategiaText}\n\nMÉTRICAS HISTÓRICAS (real vs objetivo, últimos 3 años):\n${realRows}`
          try {
            const { data, error } = await supabase.functions.invoke('ai-report', {
              body: { context, reportType: 'strategic_objective', consultancyId: id }
            })
            if (error) throw error
            const text = data?.report ?? ''
            setCorpObjForm(f => ({ ...f, generated_text: text }))
            if (corpObj) await supabase.from('strategic_objectives').update({ generated_text: text, updated_at: new Date().toISOString() }).eq('id', corpObj.id)
          } catch (e) { console.error(e) }
          setGeneratingCorpObj(false)
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-blue-800 to-blue-900 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Target size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Objetivo Estratégico Corporativo</h2>
                  <p className="text-blue-200 text-xs mt-0.5">Visión · Misión · Declaración · Estrategia · Métricas históricas · Redacción IA</p>
                </div>
              </div>
              {(isAdmin || isNVC) && (
                <button onClick={saveCorpObj} disabled={savingCorpObj}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${corpObjSaved ? 'bg-emerald-400 text-white' : 'bg-white text-blue-800 hover:bg-blue-50'}`}>
                  {savingCorpObj ? <><Loader size={14} className="animate-spin" />Guardando...</> : corpObjSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar todo</>}
                </button>
              )}
            </div>

            {/* Bloque 1 — Visión y Misión */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">1 · Visión y Misión</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-600">Visión</label>
                  <textarea rows={4} value={corpObjForm.vision} onChange={e => setCorpObjForm(f => ({ ...f, vision: e.target.value }))}
                    placeholder="¿Qué quiere ser la compañía en el futuro? ¿Cuál es su sueño de largo plazo?"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none bg-white" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-600">Misión</label>
                  <textarea rows={4} value={corpObjForm.mision} onChange={e => setCorpObjForm(f => ({ ...f, mision: e.target.value }))}
                    placeholder="¿Para qué existe la compañía? ¿A quién sirve y cómo?"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none bg-white" />
                </div>
              </div>
            </div>

            {/* Bloque 2 — Declaración */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">2 · Declaración del Objetivo</p>
              <p className="text-xs text-gray-400">¿Qué queremos lograr? En una frase clara, ambiciosa y con horizonte de tiempo.</p>
              <textarea rows={3} value={corpObjForm.declaracion} onChange={e => setCorpObjForm(f => ({ ...f, declaracion: e.target.value }))}
                placeholder="Ej: Para 2029, ser el líder del segmento premium en Colombia con 25% de participación."
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none bg-white" />
            </div>

            {/* Bloque 3 — Estrategia (lista de puntos) */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">3 · Estrategia para Lograrlo</p>
              <p className="text-xs text-gray-400">Agrega los puntos específicos de la estrategia: pilares, iniciativas prioritarias o decisiones clave.</p>

              {/* Lista de puntos existentes */}
              {corpEstrategiaPuntos.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-3">Aún no hay puntos. Agrega los que guiarán la estrategia.</p>
              )}
              <div className="space-y-2">
                {corpEstrategiaPuntos.map((pt, idx) => (
                  <div key={pt.id} className="flex gap-3 items-start group">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-1">{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      {(isAdmin || isNVC) ? (
                        <input
                          type="text"
                          value={pt.texto}
                          onChange={e => setCorpEstrategiaPuntos(prev => prev.map(p => p.id === pt.id ? { ...p, texto: e.target.value } : p))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                        />
                      ) : (
                        <p className="text-sm text-gray-800 py-2">{pt.texto}</p>
                      )}
                    </div>
                    {(isAdmin || isNVC) && (
                      <button
                        onClick={() => setCorpEstrategiaPuntos(prev => prev.filter(p => p.id !== pt.id))}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0 mt-0.5">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Input para agregar nuevo punto */}
              {(isAdmin || isNVC) && (
                <div className="border border-dashed border-blue-200 rounded-lg p-3 space-y-2 bg-blue-50/30">
                  <p className="text-xs font-semibold text-blue-600">+ Agregar punto</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newEstrategiaPoint}
                      onChange={e => setNewEstrategiaPoint(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newEstrategiaPoint.trim()) {
                          setCorpEstrategiaPuntos(prev => [...prev, { id: crypto.randomUUID(), texto: newEstrategiaPoint.trim() }])
                          setNewEstrategiaPoint('')
                        }
                      }}
                      placeholder="Ej: Expandir presencia en el canal moderno con 3 nuevos formatos"
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                    />
                    <button
                      onClick={() => {
                        if (!newEstrategiaPoint.trim()) return
                        setCorpEstrategiaPuntos(prev => [...prev, { id: crypto.randomUUID(), texto: newEstrategiaPoint.trim() }])
                        setNewEstrategiaPoint('')
                      }}
                      disabled={!newEstrategiaPoint.trim()}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
                      <Plus size={14} /> Agregar
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">También puedes presionar Enter para agregar.</p>
                </div>
              )}
            </div>

            {/* Bloque 4 — Métricas históricas con cumplimiento */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">4 · Ventas y Participación Histórica</p>
                <p className="text-xs text-gray-400 mt-0.5">Real vs. objetivo pasado y cumplimiento — últimos 3 años</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left py-2.5 px-4 w-20">Año</th>
                      <th className="text-left py-2.5 px-2">Ventas reales</th>
                      <th className="text-left py-2.5 px-2">Objetivo pasado</th>
                      <th className="text-center py-2.5 px-2 w-24">Cumplimiento</th>
                      <th className="text-left py-2.5 px-2">Part. real %</th>
                      <th className="text-left py-2.5 px-2">Part. objetivo %</th>
                      <th className="text-left py-2.5 px-2">Notas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {CORP_REAL_YEARS.map(y => {
                      const cump = cumplimiento(y)
                      const cumpColor = cump == null ? '' : cump >= 100 ? 'text-emerald-600' : cump >= 80 ? 'text-amber-600' : 'text-red-500'
                      return (
                        <tr key={y} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 px-4 text-sm font-bold text-gray-700">{y}</td>
                          <td className="py-2 px-2">
                            <div className="flex gap-1">
                              <input type="number" value={getMetric(y, 'ventas')} onChange={e => updateMetric(y, 'ventas', e.target.value)}
                                placeholder="0" className="w-20 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-center" />
                              <input type="text" value={getMetric(y, 'ventas_unit') || 'USD M'} onChange={e => updateMetric(y, 'ventas_unit', e.target.value)}
                                className="w-16 text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-gray-500" />
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="number" value={getMetric(y, 'ventas_objetivo')} onChange={e => updateMetric(y, 'ventas_objetivo', e.target.value)}
                              placeholder="0" className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-center" />
                          </td>
                          <td className="py-2 px-2 text-center">
                            {cump != null
                              ? <span className={`text-sm font-bold ${cumpColor}`}>{cump}%</span>
                              : <span className="text-xs text-gray-300">—</span>}
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative w-20">
                              <input type="number" min="0" max="100" step="0.1" value={getMetric(y, 'participacion_pct')} onChange={e => updateMetric(y, 'participacion_pct', e.target.value)}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 pr-5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-center" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative w-20">
                              <input type="number" min="0" max="100" step="0.1" value={getMetric(y, 'participacion_objetivo')} onChange={e => updateMetric(y, 'participacion_objetivo', e.target.value)}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 pr-5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-center" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" value={getMetric(y, 'notes')} onChange={e => updateMetric(y, 'notes', e.target.value)}
                              placeholder="Observación" className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-gray-500" />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400">Cumplimiento = Ventas reales ÷ Objetivo pasado × 100. Verde ≥ 100% · Ámbar ≥ 80% · Rojo &lt; 80%</p>
              </div>
            </div>

            {/* Bloque 5 — Redacción IA */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">5 · Objetivo Estratégico Redactado</p>
                  <p className="text-xs text-gray-400 mt-0.5">La IA integra toda la información en un documento ejecutivo cohesionado</p>
                </div>
                {(isAdmin || isNVC) && (
                  <button onClick={generateCorpObj} disabled={generatingCorpObj}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex-shrink-0">
                    {generatingCorpObj
                      ? <><Loader size={14} className="animate-spin" /> Redactando...</>
                      : <><Sparkles size={14} /> {corpObjForm.generated_text ? 'Re-generar' : 'Redactar con IA'}</>}
                  </button>
                )}
              </div>
              {generatingCorpObj && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                  <Loader size={16} className="animate-spin flex-shrink-0" />
                  Integrando visión, estrategia y datos históricos en el objetivo estratégico...
                </div>
              )}
              {!generatingCorpObj && corpObjForm.generated_text ? (
                <div className="space-y-3">
                  <textarea rows={14} value={corpObjForm.generated_text} onChange={e => setCorpObjForm(f => ({ ...f, generated_text: e.target.value }))}
                    className="w-full text-sm border border-blue-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none bg-blue-50/30 leading-relaxed text-gray-800" />
                  <p className="text-xs text-gray-400">Puedes editar el texto directamente. Se guarda con "Guardar todo".</p>
                </div>
              ) : !generatingCorpObj && (
                <div className="text-center py-10 text-gray-400">
                  <Target size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium text-gray-500">Completa los bloques anteriores y presiona "Redactar con IA"</p>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {/* New section modal */}
      <Modal open={sectionOpen} onClose={() => setSectionOpen(false)} title="Nueva Sección">
        <form onSubmit={createSection} className="space-y-4">
          <div>
            <label className="label">Nombre de la sección *</label>
            <input className="input" required value={sectionForm.name}
              onChange={e => setSectionForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Análisis de Datos, Encuestas, Benchmarking..." />
          </div>
          <div>
            <label className="label">Descripción (opcional)</label>
            <textarea className="input resize-none" rows={3} value={sectionForm.description}
              onChange={e => setSectionForm(f => ({ ...f, description: e.target.value }))}
              placeholder="¿Qué se hace en esta sección?" />
          </div>
          <div>
            <label className="label">Responsable de la sección</label>
            <select className="input" value={sectionForm.assignee_id}
              onChange={e => setSectionForm(f => ({ ...f, assignee_id: e.target.value }))}>
              <option value="">Sin responsable asignado</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setSectionOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingSection} className="btn-primary flex-1">
              {savingSection ? 'Creando...' : 'Crear Sección'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Duplicate section modal */}
      <Modal open={!!copySection} onClose={() => { setCopySection(null); setCopyTargetId(''); setCopyDone(false) }} title="Duplicar sección">
        <div className="space-y-4">
          {/* Source section info */}
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Sección a duplicar</p>
            <p className="font-semibold text-sm text-gray-800">{copySection?.name}</p>
            <p className="text-xs text-gray-400 mt-1">
              {copySection?.tasks?.length ?? 0} tarea{(copySection?.tasks?.length ?? 0) !== 1 ? 's' : ''} serán copiadas con estado Pendiente
            </p>
          </div>

          {/* Target phase dropdown */}
          <div>
            <label className="label">Fase destino</label>
            <select
              className="input"
              value={copyTargetId}
              onChange={e => setCopyTargetId(e.target.value)}
            >
              <option value="">Selecciona una fase...</option>
              {/* Project-level phases */}
              {allPhases.filter(p => !p.business_unit_id).length > 0 && (
                <optgroup label="── Etapas del Proyecto">
                  {allPhases.filter(p => !p.business_unit_id).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.id === phaseId ? ' (fase actual)' : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {/* BU phases grouped */}
              {[...new Map(
                allPhases.filter(p => p.business_unit_id).map(p => [p.business_unit_id, p.business_units?.name ?? 'Unidad de Negocio'])
              )].map(([bid, bName]) => (
                <optgroup key={bid} label={`── ${bName}`}>
                  {allPhases.filter(p => p.business_unit_id === bid).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.id === phaseId ? ' (fase actual)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <p className="text-xs text-gray-400">
            Se creará una nueva sección con las mismas tareas. Las tareas se reiniciarán a "Pendiente". El responsable de la sección se mantiene.
          </p>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => { setCopySection(null); setCopyTargetId(''); setCopyDone(false) }}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              onClick={duplicateSection}
              disabled={!copyTargetId || copyingSection || copyDone}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                copyDone
                  ? 'bg-emerald-500 text-white'
                  : 'btn-primary'
              }`}
            >
              {copyDone
                ? <><CheckCircle2 size={15} /> ¡Duplicada!</>
                : copyingSection
                  ? 'Duplicando...'
                  : <><Copy size={14} /> Duplicar sección</>
              }
            </button>
          </div>
        </div>
      </Modal>

      {/* New competitor modal */}
      <Modal open={competitorOpen} onClose={() => { setCompetitorOpen(false); setCompetitorForm({ name: '', description: '' }) }} title="Nuevo Competidor">
        <form onSubmit={createCompetitor} className="space-y-4">
          <div>
            <label className="label">Nombre del competidor *</label>
            <input className="input" required value={competitorForm.name}
              onChange={e => setCompetitorForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Empresa ABC, Marca XYZ..." />
          </div>
          <div>
            <label className="label">Descripción (opcional)</label>
            <textarea className="input resize-none" rows={2} value={competitorForm.description}
              onChange={e => setCompetitorForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del competidor..." />
          </div>
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 border border-gray-100">
            Se crearán automáticamente 6 secciones de análisis:<br />
            <span className="font-medium text-gray-600">Posicionamiento · Portafolio · Canales · Precio · Capacidad Industrial · Estrategia de Comunicación</span>
          </p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => { setCompetitorOpen(false); setCompetitorForm({ name: '', description: '' }) }} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingCompetitor} className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors">
              {savingCompetitor ? 'Creando...' : <><Swords size={14} /> Crear Competidor</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit competitor modal */}
      <Modal open={!!editCompetitor} onClose={() => setEditCompetitor(null)} title="Editar Competidor">
        <form onSubmit={saveEditCompetitor} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={editCompetitorForm.name}
              onChange={e => setEditCompetitorForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={editCompetitorForm.description}
              onChange={e => setEditCompetitorForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditCompetitor(null)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingCompetitor} className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors">
              {savingCompetitor ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* New consumer profile modal */}
      <Modal open={profileOpen} onClose={() => { setProfileOpen(false); setProfileForm({ name: '', description: '' }) }} title="Nuevo Perfil de Consumidor">
        <form onSubmit={createConsumerProfile} className="space-y-4">
          <div>
            <label className="label">Nombre del perfil *</label>
            <input className="input" required value={profileForm.name}
              onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Panaderías independientes, Restaurantes, Joven universitario..." />
          </div>
          <div>
            <label className="label">Descripción del segmento</label>
            <textarea className="input resize-none" rows={2}
              value={profileForm.description}
              onChange={e => setProfileForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Características principales de este segmento de consumidor..." />
          </div>
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 border border-gray-100">
            Se crearán automáticamente 4 secciones para este perfil:<br />
            <span className="font-medium text-gray-600">Necesidades y Tensiones · Sobre la Solución · Adopción de la Categoría · Adopción de Marca y Nivel de Lealtad</span>
          </p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => { setProfileOpen(false); setProfileForm({ name: '', description: '' }) }} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingProfile} className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors">
              {savingProfile ? 'Creando...' : <><Users size={14} /> Crear Perfil</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit section modal */}
      <Modal open={!!editSection} onClose={() => setEditSection(null)} title="Editar Sección">
        <form onSubmit={saveEditSection} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={editSectionForm.name}
              onChange={e => setEditSectionForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={editSectionForm.description}
              onChange={e => setEditSectionForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="label">Responsable de la sección</label>
            <select className="input" value={editSectionForm.assignee_id}
              onChange={e => setEditSectionForm(f => ({ ...f, assignee_id: e.target.value }))}>
              <option value="">Sin responsable asignado</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditSection(null)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingEdit} className="btn-primary flex-1">
              {savingEdit ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ── Competitor Card ───────────────────────────────────────────────────────────
function CompetitorCard({ competitor, navigate, consultancyId, buId, phaseId, isProjectPhase, isAdmin, isNVC, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(true)
  const [localSections, setLocalSections] = useState(
    [...(competitor.sections ?? [])].sort((a, b) => a.section_order - b.section_order)
  )
  const sections = localSections
  const completed = sections.filter(s => s.status === 'completed').length

  async function moveCompSection(index, direction) {
    const swapIndex = index + direction
    if (swapIndex < 0 || swapIndex >= sections.length) return
    const updated = [...sections]
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    const reordered = updated.map((s, i) => ({ ...s, section_order: i + 1 }))
    setLocalSections(reordered)
    const { supabase } = await import('../lib/supabase')
    await Promise.all([
      supabase.from('sections').update({ section_order: reordered[index].section_order }).eq('id', reordered[index].id),
      supabase.from('sections').update({ section_order: reordered[swapIndex].section_order }).eq('id', reordered[swapIndex].id),
    ])
  }

  const subsectionColors = [
    'bg-orange-50 border-orange-200 text-orange-700',
    'bg-blue-50 border-blue-200 text-blue-700',
    'bg-green-50 border-green-200 text-green-700',
    'bg-purple-50 border-purple-200 text-purple-700',
    'bg-yellow-50 border-yellow-200 text-yellow-700',
    'bg-pink-50 border-pink-200 text-pink-700',
  ]

  return (
    <div className="rounded-2xl border border-orange-200 bg-white overflow-hidden shadow-sm">
      {/* Competitor header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-orange-50/50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-base flex-shrink-0">
          🏢
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">{competitor.name}</p>
          {competitor.description && (
            <p className="text-xs text-gray-400 truncate">{competitor.description}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">
            {completed}/{sections.length} secciones completadas
          </p>
        </div>

        {/* Edit & Delete — admin or NVC */}
        {(isAdmin || isNVC) && (
          <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {(isAdmin || isNVC) && (
              <button onClick={onEdit}
                className="p-1.5 text-gray-300 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                title="Editar competidor">
                <Pencil size={14} />
              </button>
            )}
            {isAdmin && (
              <button onClick={onDelete}
                className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Eliminar competidor">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}

        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Sub-sections */}
      {expanded && (
        <div className="border-t border-orange-100 divide-y divide-orange-50">
          {sections.map((section, i) => {
            const doneTasks  = section.tasks?.filter(t => t.status === 'completed').length ?? 0
            const totalTasks = section.tasks?.length ?? 0
            const link = isProjectPhase
              ? `/consultancy/${consultancyId}/phase/${phaseId}/section/${section.id}`
              : `/consultancy/${consultancyId}/bu/${buId}/phase/${phaseId}/section/${section.id}`

            return (
              <div key={section.id} className="flex items-center group hover:bg-orange-50/60 transition-colors">
                {/* Up/Down reorder buttons — only for admin/NVC */}
                {(isAdmin || isNVC) && (
                  <div className="flex flex-col gap-0 pl-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => moveCompSection(i, -1)}
                      disabled={i === 0}
                      className="p-0.5 text-gray-300 hover:text-orange-500 disabled:opacity-20 disabled:cursor-not-allowed rounded transition-colors"
                      title="Subir">
                      <ChevronUp size={13} />
                    </button>
                    <button
                      onClick={() => moveCompSection(i, 1)}
                      disabled={i === sections.length - 1}
                      className="p-0.5 text-gray-300 hover:text-orange-500 disabled:opacity-20 disabled:cursor-not-allowed rounded transition-colors"
                      title="Bajar">
                      <ChevronDown size={13} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => navigate(link)}
                  className="flex-1 flex items-center gap-3 px-3 py-3 text-left"
                >
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${subsectionColors[i] ?? subsectionColors[0]} flex-shrink-0`}>
                    {section.name.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 group-hover:text-orange-700 transition-colors">
                      {section.name}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-gray-400">{doneTasks}/{totalTasks} tareas</p>
                      {(section.data_pct > 0) && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                          section.data_pct >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : section.data_pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'
                        }`}>
                          {section.data_pct}% datos
                        </span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={section.status} />
                  <ChevronRight size={14} className="text-gray-300 group-hover:text-orange-400 flex-shrink-0" />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Consumer Profile Card ─────────────────────────────────────────────────────
function ConsumerProfileCard({ profile, navigate, consultancyId, buId, phaseId, isProjectPhase, isAdmin, onDelete }) {
  const [expanded, setExpanded] = useState(true)
  const sections = profile.sections ?? []
  const completed = sections.filter(s => s.status === 'completed').length

  // Color map for the 4 sub-sections
  const subsectionColors = [
    'bg-rose-50 border-rose-200 text-rose-700',
    'bg-amber-50 border-amber-200 text-amber-700',
    'bg-sky-50 border-sky-200 text-sky-700',
    'bg-emerald-50 border-emerald-200 text-emerald-700',
  ]

  return (
    <div className="rounded-2xl border border-violet-200 bg-white overflow-hidden shadow-sm">
      {/* Profile header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-violet-50/50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center text-base flex-shrink-0">
          👤
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">{profile.name}</p>
          {profile.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{profile.description}</p>}
          <p className="text-xs text-gray-400 mt-0.5">
            {completed}/{sections.length} secciones completadas
          </p>
        </div>

        {isAdmin && (
          <button
            onClick={e => { e.stopPropagation(); onDelete() }}
            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
            title="Eliminar perfil"
          >
            <Trash2 size={14} />
          </button>
        )}

        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Sub-sections */}
      {expanded && (
        <div className="border-t border-violet-100 divide-y divide-violet-50">
          {sections.map((section, i) => {
            const doneTasks  = section.tasks?.filter(t => t.status === 'completed').length ?? 0
            const totalTasks = section.tasks?.length ?? 0
            const link = isProjectPhase
              ? `/consultancy/${consultancyId}/phase/${phaseId}/section/${section.id}`
              : `/consultancy/${consultancyId}/bu/${buId}/phase/${phaseId}/section/${section.id}`

            return (
              <button
                key={section.id}
                onClick={() => navigate(link)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50/60 transition-colors text-left group"
              >
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${subsectionColors[i] ?? subsectionColors[0]} flex-shrink-0`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 group-hover:text-violet-700 transition-colors">
                    {section.name}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs text-gray-400">{doneTasks}/{totalTasks} tareas</p>
                    {(section.data_pct > 0) && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                        section.data_pct >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : section.data_pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                      }`}>
                        {section.data_pct}% datos
                      </span>
                    )}
                  </div>
                </div>
                <StatusBadge status={section.status} />
                <ChevronRight size={14} className="text-gray-300 group-hover:text-violet-400 flex-shrink-0" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
